import React, { useState } from 'react'

import { Avatar, classnames, CrownFilled, Icon, ShapeHexagon } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, oneOf, string } from 'prop-types'

import i18n from 'Pods/Shared/I18n'
import { useUserData } from 'Pods/UnifiedNavigation/Hooks/useUserData'
import showsSubscriptionChip from 'Pods/UnifiedNavigation/Shared/Utils/showsSubscriptionChip'

import GetSubscriptionChip from './Components/GetSubscriptionChip/GetSubscriptionChip'
import GuestActions from './Components/GuestActions/GuestActions'
import { useUserMenu } from './Hooks/useUserMenu'

// UserInfoMenu is only needed once a signed-in user opens the menu, so it is
// lazy-loaded on first hover/click (mirrors the cart/inbox widget loading pattern).
let UserInfoMenu

// ShapeHexagon is wider than the crown in its viewBox, so it renders 1px smaller to fit the 15px mark.
const TIER_ICONS = {
  essentials: { svg: ShapeHexagon, size: { width: '8', height: '8' } },
  premium: { svg: CrownFilled, size: { width: '9', height: '9' } },
}

function UserMenu({
  isCurrentUser = false,
  locale = 'en',
  newRoyaltyUi = false,
  showFreeTrialTag = false,
  freeTrialLabel = '',
  subscriptionTier = null,
  plansRestructureEnabled = false,
  subscriptionState = null,
}) {
  const { userData, handleLogout } = useUserData(isCurrentUser)
  const { isOpen, setIsOpen, wrapperRef } = useUserMenu()
  const t = (key) => i18n.t(key, { ns: 'rails', lng: locale })

  const [isUserInfoMenuLoaded, setIsUserInfoMenuLoaded] = useState(false)

  const isLoaded = isUserInfoMenuLoaded && !!UserInfoMenu
  const tier = plansRestructureEnabled && TIER_ICONS[subscriptionTier] ? subscriptionTier : null
  const showSubscriptionChip = showsSubscriptionChip({ plansRestructureEnabled, subscriptionState })
  const username = userData?.attributes?.username
  const avatarLabel = tier && username
    ? i18n.t('navigation.aria.account_with_plan', {
      ns: 'rails',
      lng: locale,
      name: username,
      plan: t(`subscription_management.plan_change.tiers.${tier}`),
    })
    : username || t('navigation.aria.account')

  function loadUserInfoMenu(callback) {
    if (!UserInfoMenu && !isUserInfoMenuLoaded) {
      import('Pods/LazyLoaded/UserInfoMenu/UserInfoMenu')
        .then((module) => {
          UserInfoMenu = module.default
          setIsUserInfoMenuLoaded(true)
          callback?.()
        })
        .catch((error) => {
          console.error('Failed to load UserInfoMenu:', error)
        })
    } else {
      callback?.()
    }
  }

  function handleToggle() {
    if (isOpen) {
      setIsOpen(false)
      return
    }

    if (isLoaded) {
      setIsOpen(true)
    } else {
      loadUserInfoMenu(() => setIsOpen(true))
    }
  }

  function handleMouseEnter() {
    if (!isLoaded) {
      loadUserInfoMenu()
    }
  }

  if (!isCurrentUser) {
    return <GuestActions locale={locale} />
  }

  return (
    <div className="unified-nav__user" ref={wrapperRef}>
      {showSubscriptionChip && <GetSubscriptionChip locale={locale} />}
      <button
        type="button"
        className={classnames(
          'unified-nav__user-avatar',
          plansRestructureEnabled && 'unified-nav__user-avatar--round',
          tier && `unified-nav__user-avatar--${tier}`
        )}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={avatarLabel}
        onClick={handleToggle}
        onMouseEnter={handleMouseEnter}
      >
        {tier ? (
          <span className="unified-nav__user-avatar-ring">
            <Avatar size="32" username={username} img={userData?.attributes?.avatar?.large_url} useSingleLetter />
          </span>
        ) : (
          <Avatar size="32" username={username} img={userData?.attributes?.avatar?.large_url} useSingleLetter />
        )}
        {tier && (
          <span className="unified-nav__user-avatar-mark" aria-hidden="true">
            <Icon
              svgComponent={TIER_ICONS[tier].svg}
              className="unified-nav__user-avatar-glyph"
              size={TIER_ICONS[tier].size}
              notSpaced
            />
          </span>
        )}
      </button>

      {isOpen && isLoaded && (
        <UserInfoMenu
          userData={userData}
          locale={locale}
          handleLogout={handleLogout}
          newRoyaltyUi={newRoyaltyUi}
          showFreeTrialTag={showFreeTrialTag}
          freeTrialLabel={freeTrialLabel}
        />
      )}
    </div>
  )
}

UserMenu.propTypes = {
  isCurrentUser: bool,
  locale: string,
  newRoyaltyUi: bool,
  showFreeTrialTag: bool,
  freeTrialLabel: string,
  subscriptionTier: oneOf(['essentials', 'premium']),
  plansRestructureEnabled: bool,
  subscriptionState: oneOf(['active', 'paused', 'payment_failed', 'none']),
}

export default UserMenu
